<template>
  <div class="pixso-page solution-index">
    <main class="solution-index__main">
      <section class="solution-index__hero" aria-labelledby="solution-hero-title">
        <div class="solution-index__hero-copy">
          <h1 id="solution-hero-title" class="solution-index__title">
            {{ t('SolutionIndex.hero.title') }}
          </h1>
          <p class="solution-index__desc">
            {{ t('SolutionIndex.hero.description') }}
          </p>
        </div>
      </section>

      <section id="solution-scenarios" class="solution-index__cards pixso-stage" :aria-label="t('SolutionIndex.cards.ariaLabel')">
        <div class="solution-index__card-grid">
          <router-link
            v-for="item in solutionItems"
            :key="item.title"
            class="solution-index__card"
            :to="item.to"
            :aria-label="`${item.title} ${t('SolutionIndex.cards.more')}`"
          >
            <img
              class="solution-index__card-image"
              :src="item.image"
              :style="{ objectPosition: item.imagePosition || 'center' }"
              alt=""
              aria-hidden="true"
            />
            <span class="solution-index__card-mask" aria-hidden="true"></span>
            <span class="solution-index__card-copy">
              <span class="solution-index__card-title">{{ item.title }}</span>
              <span class="solution-index__card-more">{{ t('SolutionIndex.cards.more') }} &gt;</span>
            </span>
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import airGroundImage from '@/assets/images/pixso-solution-index/cards/air-ground.jpg'
import collaborativeInspectionImage from '@/assets/images/pixso-solution-index/cards/collaborative-inspection.jpg'
import smartDrivingImage from '@/assets/images/pixso-solution-index/cards/smart-driving.jpg'
import traditionalMachineryImage from '@/assets/images/pixso-solution-index/cards/traditional-machinery.jpg'

const { t } = useI18n()

const solutionItems = computed(() => [
  {
    title: t('SolutionIndex.cards.smartDriving.title'),
    image: smartDrivingImage,
    imagePosition: '42% center',
    to: '/solution/smart-driving'
  },
  {
    title: t('SolutionIndex.cards.collaborativeInspection.title'),
    image: collaborativeInspectionImage,
    to: '/solution/collaborative-inspection'
  },
  {
    title: t('SolutionIndex.cards.traditionalMachinery.title'),
    image: traditionalMachineryImage,
    to: '/solution/traditional-machinery-intelligent-operation'
  },
  {
    title: t('SolutionIndex.cards.cargoDelivery.title'),
    image: airGroundImage,
    to: '/solution/air-ground-collaboration'
  }
])
</script>

<style scoped>
.solution-index {
  --solution-index-hero-copy-max: 1200px;
  --solution-index-hero-bg: url("@/assets/images/pixso-solution-index/hero-product-inertia.webp");
  --solution-index-hero-copy-y: -2px;
  --solution-index-card-ratio: 884 / 811;
  --solution-index-card-grid-padding: var(--pixso-page-padding);
  --solution-index-card-row-gap: calc(var(--pixso-gap-lg) + var(--pixso-fs-16));
  --solution-index-card-overlay: linear-gradient(180deg, rgba(0, 0, 0, 0.04) 18%, rgba(0, 0, 0, 0.72) 100%);
  --solution-index-title-mobile-size: var(--pixso-fs-36);
  --solution-index-title-mobile-line: var(--pixso-lh-36);
  --solution-index-card-title-mobile-size: var(--pixso-fs-24);
  --solution-index-card-title-mobile-line: var(--pixso-lh-24);
  --solution-index-card-text-mobile-size: var(--pixso-fs-14);
  --solution-index-card-text-mobile-line: var(--pixso-lh-14);
  --solution-index-cards-bottom-tablet: 220px;
  --solution-index-cards-bottom-mobile: 120px;
}

.solution-index__main {
  width: 100%;
  background: var(--pixso-color-bg);
}

.solution-index__hero {
  width: 100%;
  height: var(--pixso-hero-height);
  min-height: var(--pixso-hero-min);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--solution-index-hero-bg) center / cover no-repeat;
}

.solution-index__hero-copy {
  width: min(calc(100% - var(--pixso-page-padding) * 2), var(--solution-index-hero-copy-max));
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pixso-gap-md);
  padding: 0 var(--pixso-fs-96);
  text-align: center;
  transform: translateY(var(--solution-index-hero-copy-y));
}

.solution-index__title {
  margin: 0;
}

.solution-index__title {
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-96);
  font-weight: 700;
  line-height: var(--pixso-lh-96);
  letter-spacing: 0;
}

.solution-index__desc {
  max-width: var(--solution-index-hero-copy-max);
  margin: 0;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-20);
  font-weight: 700;
  line-height: var(--pixso-lh-20);
  letter-spacing: 0;
}

.solution-index__cards {
  padding: var(--pixso-fs-96) 0 var(--pixso-section-gap-xl);
}

.solution-index__card-grid {
  width: min(100%, var(--pixso-page-max));
  margin: 0 auto;
  padding: 0 var(--solution-index-card-grid-padding);
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--solution-index-card-row-gap) var(--pixso-gap-md);
}

.solution-index__card {
  position: relative;
  aspect-ratio: var(--solution-index-card-ratio);
  display: block;
  overflow: hidden;
  border-radius: var(--pixso-radius-lg);
  color: var(--pixso-color-text);
  cursor: pointer;
  text-decoration: none;
  background: var(--pixso-color-surface);
}

.solution-index__card-image,
.solution-index__card-mask {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.solution-index__card-image {
  object-fit: cover;
}

.solution-index__card-mask {
  background: var(--solution-index-card-overlay);
}

.solution-index__card-copy {
  position: absolute;
  left: var(--pixso-gap-lg);
  right: var(--pixso-gap-lg);
  bottom: var(--pixso-gap-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  text-align: center;
}

.solution-index__card-title {
  font-size: var(--pixso-fs-36);
  font-weight: 700;
  line-height: var(--pixso-lh-36);
}

.solution-index__card-more {
  margin-top: var(--pixso-gap-xs);
  font-size: var(--pixso-fs-16);
  font-weight: 700;
  line-height: var(--pixso-lh-16);
}

@media (max-width: 1200px) {
  .solution-index__hero {
    height: var(--pixso-hero-height);
    min-height: var(--pixso-hero-min);
  }

  .solution-index__hero-copy {
    padding: 0;
  }

  .solution-index__cards {
    padding-top: var(--pixso-fs-72);
    padding-bottom: var(--solution-index-cards-bottom-tablet);
  }
}

@media (max-width: 768px) {
  .solution-index__hero {
    height: var(--pixso-hero-mobile);
    min-height: var(--pixso-hero-mobile);
    background-position: 58% center;
  }

  .solution-index__hero-copy {
    gap: var(--pixso-fs-16);
    padding: 0;
  }

  .solution-index__title {
    font-size: var(--pixso-fs-36);
    line-height: var(--pixso-lh-36);
  }

  .solution-index__desc {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }

  .solution-index__cards {
    padding-top: var(--pixso-gap-lg);
    padding-bottom: var(--solution-index-cards-bottom-mobile);
  }

  .solution-index__card-grid {
    grid-template-columns: 1fr;
    padding: 0 var(--pixso-page-padding);
    gap: var(--pixso-gap-md);
  }

  .solution-index__card {
    border-radius: var(--pixso-radius-md);
  }

  .solution-index__card-copy {
    left: var(--pixso-gap-md);
    right: var(--pixso-gap-md);
    bottom: var(--pixso-gap-md);
  }

  .solution-index__card-title {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .solution-index__card-more {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }
}
</style>
